import { IFilterSelectorOpts, PublisherOrCampaign } from '../../home/dashboard-content/types'

type LegacySelectedStrings = true | string[]
type LegacySelectedItems = true | PublisherOrCampaign[]

export type ScriptOptionSets = {
    campaigns: PublisherOrCampaign[]
    networks: string[]
    countries: string[]
    channels: PublisherOrCampaign[]
}

export type DisallowedRuleMode = 'all' | 'pageviews' | 'clicks'

export type DisallowedDomainRule = {
    domain: string
    path: string
    mode: DisallowedRuleMode
}

export type ScriptSettingsState = {
    name: string
    allowedDomains: string[]
    disallowedDomainRules: DisallowedDomainRule[]
    ignoredPages: string[]
    trackExistingAffiliateLinks: boolean
    filters: IFilterSelectorOpts
    deselected: string[]
}

export type ScriptSettingsPayload = {
    name?: string
    allowedDomains: string[]
    ignoredPages: string[]
    disallowedPathsByDomain: Record<string, Array<string | { path: string; mode?: string }>>
    trackExistingAffiliateLinks?: boolean
    campaigns?: LegacySelectedItems
    countries?: LegacySelectedStrings
    networks?: LegacySelectedStrings
    channels?: LegacySelectedItems
    deselectedCampaigns?: PublisherOrCampaign[]
    deselectedCountries?: string[]
    deselectedNetworks?: string[]
    deselectedChannels?: PublisherOrCampaign[]
    deselected: string[]
}

function cloneAllOptions(options: ScriptOptionSets): IFilterSelectorOpts {
    return {
        campaigns: [...options.campaigns],
        channels: [...options.channels],
        countries: [...options.countries],
        networks: [...options.networks],
        publishers: [],
    }
}

function hasConfiguredScriptSettings(value: any): boolean {
    if (!value || typeof value !== 'object') return false

    if (value.trackExistingAffiliateLinks === true) return true

    if (value.campaigns === true || value.channels === true || value.countries === true || value.networks === true) {
        return true
    }

    if (Array.isArray(value.allowedDomains) && value.allowedDomains.length > 0) return true
    if (Array.isArray(value.ignoredPages) && value.ignoredPages.length > 0) return true
    if (Array.isArray(value.campaigns) && value.campaigns.length > 0) return true
    if (Array.isArray(value.channels) && value.channels.length > 0) return true
    if (Array.isArray(value.countries) && value.countries.length > 0) return true
    if (Array.isArray(value.networks) && value.networks.length > 0) return true
    if (Array.isArray(value.deselectedCampaigns) && value.deselectedCampaigns.length > 0) return true
    if (Array.isArray(value.deselectedChannels) && value.deselectedChannels.length > 0) return true
    if (Array.isArray(value.deselectedCountries) && value.deselectedCountries.length > 0) return true
    if (Array.isArray(value.deselectedNetworks) && value.deselectedNetworks.length > 0) return true
    if (Array.isArray(value.deselected) && value.deselected.length > 0) return true

    return !!(value.disallowedPathsByDomain && Object.keys(value.disallowedPathsByDomain).length > 0)
}

function hasOwnProperty(value: unknown, key: string): boolean {
    return !!value && Object.prototype.hasOwnProperty.call(value, key)
}

export function isSameItem(a: any, b: any): boolean {
    if (typeof a === 'string' && typeof b === 'string') return a === b
    return a.id === b.id && a.network === b.network && a.country === b.country
}

export function isAllSelected<T>(
    selected: T[],
    all: T[],
    compareFn: (a: T, b: T) => boolean
) {
    if (selected.length !== all.length) return false
    return all.every(a => selected.some(b => compareFn(a, b)))
}

export function normalizeRuleMode(value: unknown): DisallowedRuleMode {
    const normalized = String(value || '').trim().toLowerCase()
    if (normalized === 'pageviews') return 'pageviews'
    if (normalized === 'clicks') return 'clicks'
    if (normalized === 'both') return 'all'
    return 'all'
}

function normalizeDisallowedDomainPath(domain: string, rawPath: string): DisallowedDomainRule | null {
    const cleanedDomain = domain.trim().toLowerCase()
    if (!/^[a-z0-9.-]+\.[a-z]{2,}$/i.test(cleanedDomain)) return null

    let path = rawPath.trim().split(/[?#]/)[0] || '/'
    if (!path.startsWith('/')) path = `/${path}`
    path = path.replace(/\/+/g, '/')

    if (path !== '/' && path.endsWith('/') && !path.endsWith('/*')) {
        path = path.slice(0, -1)
    }

    if (path.endsWith('/*')) {
        const prefix = path.slice(0, -2).replace(/\/+$/, '')
        path = `${prefix || ''}/*`
        if (!path.startsWith('/')) path = `/${path}`
    }

    return { domain: cleanedDomain, path, mode: 'all' }
}

export function flattenDisallowedPathsByDomain(value: unknown, allowedDomains: string[]): DisallowedDomainRule[] {
    if (!value || typeof value !== 'object') return []

    const allowedSet = new Set(allowedDomains)
    const out: DisallowedDomainRule[] = []

    for (const [domain, patterns] of Object.entries(value as Record<string, unknown>)) {
        if (!allowedSet.has(domain) || !Array.isArray(patterns)) continue

        for (const pattern of patterns) {
            if (typeof pattern === 'string') {
                const normalized = normalizeDisallowedDomainPath(domain, pattern)
                if (!normalized) continue
                out.push(normalized)
                continue
            }

            if (!pattern || typeof pattern !== 'object') continue
            const path = typeof (pattern as { path?: unknown }).path === 'string'
                ? (pattern as { path: string }).path
                : ''

            const normalized = normalizeDisallowedDomainPath(domain, path)
            if (!normalized) continue
            out.push({ ...normalized, mode: normalizeRuleMode((pattern as { mode?: unknown }).mode) })
        }
    }

    const deduped = new Map<string, DisallowedDomainRule>()
    for (const rule of out) {
        deduped.set(`${rule.domain}${rule.path}`, rule)
    }

    return Array.from(deduped.values())
}

export function toDisallowedPathsByDomain(entries: DisallowedDomainRule[], allowedDomains: string[]): Record<string, Array<string | { path: string; mode?: string }>> {
    const allowedSet = new Set(allowedDomains)
    const out: Record<string, Array<string | { path: string; mode?: string }>> = {}

    for (const entry of entries) {
        const normalized = normalizeDisallowedDomainPath(entry.domain, entry.path)
        if (!normalized) continue
        if (!allowedSet.has(normalized.domain)) continue

        if (!out[normalized.domain]) out[normalized.domain] = []

        const existing = out[normalized.domain].some((item) => {
            if (typeof item === 'string') return item === normalized.path
            return item.path === normalized.path
        })
        if (existing) continue

        const mode = normalizeRuleMode(entry.mode)
        out[normalized.domain].push(mode === 'all' ? normalized.path : { path: normalized.path, mode })
    }

    return out
}

function normalizeStringArray(value: unknown, { lowerCase = false } = {}) {
    if (!Array.isArray(value)) return []

    const cleaned = value
        .filter((item): item is string => typeof item === 'string')
        .map(item => item.trim())
        .filter(Boolean)
        .map(item => (lowerCase ? item.toLowerCase() : item))

    return Array.from(new Set(cleaned))
}

function normalizeSelectedStrings(value: unknown, all: string[]) {
    if (value === true) return [...all]
    const selected = normalizeStringArray(value)
    return all.filter(option => selected.includes(option))
}

function normalizeDeselectedStrings(value: unknown, all: string[]) {
    const deselected = normalizeStringArray(value)
    return all.filter(option => !deselected.includes(option))
}

function normalizeSelectedItems(value: unknown, all: PublisherOrCampaign[]) {
    if (value === true) return [...all]
    if (!Array.isArray(value)) return []

    return all.filter(option => value.some(item => isSameItem(option, item)))
}

function normalizeDeselectedItems(value: unknown, all: PublisherOrCampaign[]) {
    if (!Array.isArray(value)) return [...all]

    return all.filter(option => !value.some(item => isSameItem(option, item)))
}

function serializeDeselectedStrings(selected: string[], all: string[]) {
    return all.filter(option => !selected.includes(option))
}

function serializeDeselectedItems(selected: PublisherOrCampaign[], all: PublisherOrCampaign[]) {
    return all.filter(option => !selected.some(item => isSameItem(option, item)))
}

export function createEmptyScriptSettings(): ScriptSettingsState {
    return {
        name: '',
        allowedDomains: [],
        disallowedDomainRules: [],
        ignoredPages: [],
        trackExistingAffiliateLinks: false,
        filters: {
            campaigns: [],
            channels: [],
            countries: [],
            networks: [],
            publishers: [],
        },
        deselected: [],
    }
}

export function createDefaultScriptSettings(options: ScriptOptionSets): ScriptSettingsState {
    return {
        name: '',
        allowedDomains: [],
        disallowedDomainRules: [],
        ignoredPages: [],
        trackExistingAffiliateLinks: false,
        filters: cloneAllOptions(options),
        deselected: [],
    }
}

export function normalizeScriptSettingsState(
    value: any,
    options: ScriptOptionSets
): ScriptSettingsState {
    if (!hasConfiguredScriptSettings(value)) {
        return createDefaultScriptSettings(options)
    }

    const allowedDomains = normalizeStringArray(value?.allowedDomains, { lowerCase: true })

    return {
        name: typeof value?.name === 'string' ? value.name.trim().slice(0, 80) : '',
        allowedDomains,
        disallowedDomainRules: flattenDisallowedPathsByDomain(value?.disallowedPathsByDomain, allowedDomains),
        ignoredPages: normalizeStringArray(value?.ignoredPages),
        trackExistingAffiliateLinks: value?.trackExistingAffiliateLinks === true,
        filters: {
            campaigns: hasOwnProperty(value, 'deselectedCampaigns')
                ? normalizeDeselectedItems(value?.deselectedCampaigns, options.campaigns)
                : normalizeSelectedItems(value?.campaigns, options.campaigns),
            channels: hasOwnProperty(value, 'deselectedChannels')
                ? normalizeDeselectedItems(value?.deselectedChannels, options.channels)
                : normalizeSelectedItems(value?.channels, options.channels),
            countries: hasOwnProperty(value, 'deselectedCountries')
                ? normalizeDeselectedStrings(value?.deselectedCountries, options.countries)
                : normalizeSelectedStrings(value?.countries, options.countries),
            networks: hasOwnProperty(value, 'deselectedNetworks')
                ? normalizeDeselectedStrings(value?.deselectedNetworks, options.networks)
                : normalizeSelectedStrings(value?.networks, options.networks),
            publishers: [],
        },
        deselected: normalizeStringArray(value?.deselected),
    }
}

export function serializeScriptSettingsState(
    value: ScriptSettingsState,
    options: ScriptOptionSets
): ScriptSettingsPayload {
    return {
        name: value.name.trim(),
        allowedDomains: Array.from(new Set(value.allowedDomains.map(domain => domain.trim().toLowerCase()).filter(Boolean))),
        disallowedPathsByDomain: toDisallowedPathsByDomain(value.disallowedDomainRules, value.allowedDomains),
        ignoredPages: value.ignoredPages,
        trackExistingAffiliateLinks: value.trackExistingAffiliateLinks === true,
        deselectedCampaigns: serializeDeselectedItems(value.filters.campaigns, options.campaigns),
        deselectedChannels: serializeDeselectedItems(value.filters.channels, options.channels),
        deselectedCountries: serializeDeselectedStrings(value.filters.countries, options.countries),
        deselectedNetworks: serializeDeselectedStrings(value.filters.networks, options.networks),
        deselected: value.deselected,
    }
}